import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Sun, Coffee, Moon, Sparkles, FileText, Users,
  CheckCircle, AlertTriangle, ChevronLeft, Briefcase,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  'X-Company-ID': localStorage.getItem('company_id') || '',
});

const GREETING_MAP = {
  morning:   { text: 'صباح الخير',  icon: Coffee },
  afternoon: { text: 'مساء الخير',  icon: Sun },
  evening:   { text: 'مساء النور',  icon: Moon },
};

const fmtNumber = (v) => new Intl.NumberFormat('ar-SA', { maximumFractionDigits: 0 }).format(Number(v) || 0);
const fmtAmount = (v) => new Intl.NumberFormat('ar-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0);

/**
 * DailyDigestCard — Section 2.1 additive widget for Dashboard overview.
 *
 * Pulls /api/dashboard/daily-digest and renders:
 *  - Greeting + user name + today's date
 *  - Last 24h activity counters (from event_log)
 *  - Today's priorities (overdue invoices, pending approvals)
 *
 * Pure additive: does not replace any existing widget.
 */
export default function DailyDigestCard({ className = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const r = await axios.get(`${base}/api/dashboard/daily-digest`, { headers: getAuthHeaders() });
      setData(r.data || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'تعذر تحميل النظرة اليومية');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div
        className={cn(
          'rounded-2xl border border-slate-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5 shadow-sm',
          className,
        )}
        dir="rtl"
      >
        <div className="animate-pulse space-y-3">
          <div className="h-5 w-1/3 rounded bg-slate-200 dark:bg-neutral-800" />
          <div className="h-3 w-1/2 rounded bg-slate-200 dark:bg-neutral-800" />
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="h-12 rounded bg-slate-100 dark:bg-neutral-800" />
            <div className="h-12 rounded bg-slate-100 dark:bg-neutral-800" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return null;
  }

  const greeting = GREETING_MAP[data.greeting_time] || GREETING_MAP.morning;
  const GreetIcon = greeting.icon;
  const last24 = data.last_24h || {};
  const priorities = Array.isArray(data.priorities) ? data.priorities : [];

  const counters = [
    { key: 'new_invoices',       label: 'فواتير جديدة',     icon: FileText,    value: last24.new_invoices,       extra: last24.new_invoices_amount > 0 ? `${fmtAmount(last24.new_invoices_amount)} د.إ` : null },
    { key: 'received_payments',  label: 'مدفوعات مستلمة',   icon: CheckCircle, value: last24.received_payments,  extra: null },
    { key: 'new_employees',      label: 'موظفون جدد',       icon: Users,       value: last24.new_employees,      extra: null },
    { key: 'completed_projects', label: 'مشاريع مكتملة',    icon: Briefcase,   value: last24.completed_projects, extra: null },
  ];
  const visibleCounters = counters.filter((c) => (Number(c.value) || 0) > 0);

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-primary-100 dark:border-primary-900/40 shadow-sm',
        'bg-gradient-to-l from-primary-50 via-white to-violet-50',
        'dark:from-primary-950/40 dark:via-neutral-900 dark:to-violet-950/40',
        'p-5',
        className,
      )}
      dir="rtl"
      role="region"
      aria-label="نظرة اليوم"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/80 dark:bg-neutral-900/80 ring-1 ring-primary-200 dark:ring-primary-800 flex-shrink-0">
            <GreetIcon size={18} className="text-amber-500" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">
              {greeting.text}، {data.user_name}
            </p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              {data.today_iso}
            </p>
          </div>
        </div>
        <span className="hidden sm:flex items-center gap-1 text-[10px] uppercase tracking-wider text-violet-600 dark:text-violet-300 bg-violet-100/70 dark:bg-violet-900/40 px-2 py-0.5 rounded-full">
          <Sparkles size={11} aria-hidden="true" />
          نظرة اليوم
        </span>
      </div>

      {/* Last 24h activity */}
      {visibleCounters.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
            آخر 24 ساعة
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {visibleCounters.map(({ key, label, icon: Ico, value, extra }) => (
              <div
                key={key}
                className="rounded-xl bg-white/70 dark:bg-neutral-900/60 ring-1 ring-slate-100 dark:ring-neutral-800 p-3"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Ico size={13} className="text-primary-600 dark:text-primary-300" aria-hidden="true" />
                  <span className="text-[11px] text-slate-600 dark:text-slate-300 truncate">{label}</span>
                </div>
                <p className="text-xl font-black text-slate-900 dark:text-slate-100 tabular-nums">
                  {fmtNumber(value)}
                </p>
                {extra && (
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 tabular-nums truncate">{extra}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {visibleCounters.length === 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
          لا أحداث جديدة في آخر 24 ساعة.
        </p>
      )}

      {/* Priorities */}
      {priorities.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
            🎯 أولوياتك اليوم
          </p>
          <div className="space-y-1.5">
            {priorities.map((p, idx) => (
              <Link
                key={p.type || idx}
                to={p.link || '/dashboard'}
                title={`${p.label} — ${p.count}`}
                className="group flex items-center gap-2 rounded-xl bg-white/80 dark:bg-neutral-900/70 ring-1 ring-amber-100 dark:ring-amber-900/40 hover:ring-amber-200 dark:hover:ring-amber-800/60 hover:bg-white dark:hover:bg-neutral-900 px-3 py-2 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-300"
              >
                <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400 flex-shrink-0" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex-1 truncate">
                  {p.label}: {fmtNumber(p.count)}
                  {p.amount > 0 && (
                    <span className="text-xs text-slate-500 dark:text-slate-400 mx-1 tabular-nums">
                      ({fmtAmount(p.amount)} د.إ)
                    </span>
                  )}
                </span>
                <ChevronLeft size={14} className="text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
